/**
 * HeritageArticlePage — public sub-page for /indian-heritage/:slug.
 *
 * Rendered on the IFT brand. If the heritage_articles doc for this slug
 * hasn't been enriched by the content agent yet, we display a friendly
 * "composing" state that reflects the actual pipeline (fetching text +
 * photos in the background) without leaking implementation details.
 *
 * Polls every 15s while `content_agent_pending` is true so the page
 * flips to the finished editorial the moment it lands.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Share2, RefreshCw, Feather } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import ArticleReaderToolbar from "@/components/reader/ArticleReaderToolbar";

const API = process.env.REACT_APP_BACKEND_URL;

// Parchment palette — matches the rest of the IFT surface.
const P = {
  bg:      "#FDF9EF",
  bgDeep:  "#F4E9CE",
  ink:     "#3A1F0F",
  inkSoft: "#6B4A2E",
  saffron: "#B25D38",
  green:   "#0E7C55",
  gold:    "#C08A2E",
  ivory:   "#FFF8E6",
};

function Ornament({ char = "❦" }) {
  return (
    <div className="flex items-center gap-3 my-4" aria-hidden>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.gold}55, ${P.gold}, ${P.gold}55, transparent)` }} />
      <span className="text-lg" style={{ color: P.gold }}>{char}</span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.gold}55, ${P.gold}, ${P.gold}55, transparent)` }} />
    </div>
  );
}

export default function HeritageArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/heritage-articles/${encodeURIComponent(slug)}`,
      );
      setArticle(data);
    } catch {
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Poll while pending — the content agent runs in the background.
  useEffect(() => {
    if (!article?.content_agent_pending) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [article?.content_agent_pending, load]);

  const shareAsCard = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.share) {
        await navigator.share({ title: article?.title || "Indian Heritage", url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    } catch { /* user cancelled or clipboard unavailable — silent */ }
  };

  const title = article?.title || (slug ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Indian Heritage");
  const isPending = !loading && article?.content_agent_pending && !(article?.body_html || article?.content_html);
  const body = article?.body_html || article?.content_html || "";

  return (
    <div className="min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="heritage-article-page">
      <SeoHead
        section="heritage"
        title={`${title} · Indian Heritage`}
        description={article?.hook || `An IndianFamilyTree.com heritage note on ${title}.`}
      />

      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <Link
          to="/indian-heritage"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-4"
          data-testid="heritage-article-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All heritage notes
        </Link>

        <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: P.saffron }}>
          Indian Heritage · IndianFamilyTree.com
        </div>
        <h1 className="font-display italic text-4xl sm:text-5xl leading-tight" style={{ color: P.ink }} data-testid="heritage-article-title">
          {title}
        </h1>
        {article?.hook && (
          <p className="mt-3 text-base italic" style={{ color: P.inkSoft }} data-testid="heritage-article-hook">
            {article.hook}
          </p>
        )}

        <Ornament />

        {/* Share + regenerate row */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            onClick={shareAsCard}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow"
            style={{ background: P.green }}
            data-testid="heritage-share-card"
          >
            <Share2 className="w-4 h-4" /> {copied ? "Link copied" : "Share as card"}
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-mono uppercase tracking-widest border-2"
            style={{ borderColor: P.gold, color: P.saffron, background: "transparent" }}
            data-testid="heritage-regenerate"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isPending ? "animate-spin" : ""}`} /> Regenerate
          </button>
        </div>

        {/* Composing state — the exact wording brand-approved for the
            IFT heritage sub-pages. Kept intentionally free of implementation
            names (no model or corpus references) so the reader hears the
            archive's voice, not the toolchain's. */}
        {isPending && (
          <section
            className="rounded-2xl border-2 p-6 sm:p-8"
            style={{ background: P.ivory, borderColor: `${P.gold}66` }}
            data-testid="heritage-composing"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: P.saffron }} />
              <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>
                Composing a new heritage note
              </span>
            </div>
            <h2 className="font-display italic text-3xl sm:text-4xl mb-3" style={{ color: P.ink }}>
              We are writing about {title}…
            </h2>
            <p className="text-base leading-relaxed" style={{ color: P.inkSoft }}>
              We're fetching a fresh editorial and photographs. This usually
              takes about thirty seconds. You can stay here — the page will
              update the moment the archive is ready.
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs" style={{ color: P.gold }}>
              <Feather className="w-3.5 h-3.5" />
              <span className="font-mono uppercase tracking-widest">Auto-refreshing every 15 seconds</span>
            </div>
          </section>
        )}

        {/* Body when ready */}
        {!isPending && body && (
          <>
            <ArticleReaderToolbar
              bodyRef={bodyRef}
              title={article?.title || slug}
              source_lang={(article && article.language) || "en"}
              storageKey={`heritage:${slug}`}
            />
            <article
              ref={bodyRef}
              className="prose-article"
              data-testid="heritage-article-body"
              dangerouslySetInnerHTML={{ __html: body }}
            />
          </>
        )}

        {/* 404-ish state — content agent hasn't been queued and no stub */}
        {!loading && !article && (
          <section className="text-center py-14" data-testid="heritage-article-empty">
            <div className="font-display italic text-2xl mb-2" style={{ color: P.ink }}>
              This heritage note hasn't been written yet.
            </div>
            <p className="text-sm mb-4" style={{ color: P.inkSoft }}>
              Come back in a little while — the archive is being tended to.
            </p>
            <Link
              to="/indian-heritage"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white"
              style={{ background: P.saffron }}
            >
              Browse the full archive <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </section>
        )}

        {/* Sources footer — only when we have them */}
        {Array.isArray(article?.sources) && article.sources.length > 0 && (
          <section className="mt-12 pt-6 border-t" style={{ borderColor: `${P.gold}55` }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: P.saffron }}>
              Sources
            </div>
            <ul className="space-y-1 text-sm" style={{ color: P.inkSoft }}>
              {article.sources.map((s, i) => (
                <li key={i} className="italic">• {s}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
